import { Card, CardContent, CardHeader, CardTitle } from '../../card';
import { Button } from '../../button/button';
import { Link } from 'react-router-dom';
import { Play, Plug, StopCircle, SquarePlus } from 'lucide-react';
import { Progress } from '../../progress';
import { MultiSelect } from '../../multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../select';
import { SortButton } from '../../sort-button/sort-button';
import { SimulationSettingsProps } from './types';
import { useMemo } from 'react';
import { EntityType } from '../../../../lib/enums';
import { comparePersonasBySortOrder } from '../../../../lib/utils/sort-personas';
import { useGlobal } from '../../../../contexts/GlobalProvider';
import { getCurrentTurns } from '../../../../lib/utils/conversation-simulation/get-current-turns';
import { useSimulationStore } from '../../../../stores/simulationStore';
import { useAgents } from '../../../../contexts/AgentContext';
import { isConnectedAgent } from '../../../../lib/edge-functions';
import { usePersonas } from '../../../../contexts/PersonaContext';

export function SimulationSettings({
  selectedAgent,
  setSelectedAgent,
  conversationTurns,
  setConversationTurns,
  isStopping,
  startSimulation,
  stopSimulation,
  newSimulation,
  isMaxTurnsReached,
  maxTurns,
}: SimulationSettingsProps): JSX.Element {
  const { hiddenExamples, personaSortOrder, setPersonaSortOrder } = useGlobal();
  const { agents } = useAgents();
  const { personas } = usePersonas();

  const {
    isSimulating,
    simulationProgress,
    selectedPersonas,
    selectedPersonaTab,
    selectedSimulation,
    personaMessages,
    setSimulationState,
  } = useSimulationStore();

  // Filter out hidden agents and personas
  const visibleAgents = useMemo(() => {
    const isHidden = hiddenExamples.includes(EntityType.Agents);
    const filtered = isHidden ? agents.filter((a) => !a.is_example) : agents;
    return filtered.filter((a) => a.status !== 'draft');
  }, [agents, hiddenExamples]);

  const visiblePersonas = useMemo(() => {
    const isHidden = hiddenExamples.includes(EntityType.Personas);
    const filtered = isHidden
      ? personas.filter((p) => !p.is_example)
      : personas;
    // Exclude draft personas from simulation selector
    const publishedOnly = filtered.filter((p) => p.status !== 'draft');

    // Sort based on selected sort order (updated_at = newest first)
    const sorted = [...publishedOnly].sort((a, b) =>
      comparePersonasBySortOrder(a, b, personaSortOrder, 'updated_at'),
    );

    return sorted;
  }, [personas, hiddenExamples, personaSortOrder]);

  const getMinTurns = () => {
    if (!selectedSimulation || !selectedPersonaTab) return 1;
    const messages = personaMessages[selectedPersonaTab] || [];
    // Continuing means running at least one turn beyond what's already done,
    // which is exactly what startSimulation's `<= currentTurns` guard requires.
    return getCurrentTurns(messages) + 1;
  };

  return (
    <Card
      className="rounded-[10px] border border-solid border-[#00000020] bg-prompt-safecomfantasy shadow-[0px_2px_4px_#0000001a]"
      data-onboarding="simulation-settings"
      // The guided tour highlights one control at a time from this.
      data-onboarding-stage={
        !selectedAgent
          ? 'agent'
          : !selectedPersonas?.length
            ? 'personas'
            : 'start'
      }
    >
      <CardHeader>
        <CardTitle className="font-prompt-safe-com-semantic-heading-5 text-prompt-safecomvulcan">
          Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="font-prompt-safe-com-semantic-label text-prompt-safecomvulcan">
            Select agent
          </label>
          <div id="agent-selector">
            <MultiSelect
              options={visibleAgents.map((a) => ({
                value: a.id,
                label: a.name,
                // Connected agents have no short tag; say what they are instead.
                short_tag: isConnectedAgent(a)
                  ? a.short_tag
                    ? `${a.short_tag} · connected`
                    : 'Connected'
                  : a.short_tag,
              }))}
              selected={selectedAgent ? [selectedAgent] : []}
              onChange={(values: string[]) =>
                setSelectedAgent(values[0] || null)
              }
              placeholder="Choose an agent"
              popoverClassName="w-[270px]"
              disabled={!!selectedSimulation}
              chevronClassName="hidden"
              singleSelect
              className="overflow-hidden"
            />
          </div>
          {!selectedSimulation && (
            <Link
              to="/connections"
              className="inline-flex items-center gap-1.5 text-xs text-prompt-safecomoutrageous-orange hover:underline"
            >
              <Plug className="h-3.5 w-3.5" />
              Agent not here? Connect one
            </Link>
          )}
        </div>
        <div className="space-y-2">
          <div className="flex justify-between">
            <label className="font-prompt-safe-com-semantic-label text-prompt-safecomvulcan">
              Select personas
            </label>
            {selectedPersonas?.length > 0 &&
              !selectedSimulation &&
              !isSimulating && (
                <button
                  className="text-xs text-prompt-safecomoutrageous-orange underline"
                  onClick={() =>
                    setSimulationState({
                      selectedPersonas: [],
                      selectedPersonaTab: null,
                    })
                  }
                >
                  Deselect all ({selectedPersonas?.length})
                </button>
              )}
          </div>
          <div className="flex items-center gap-2">
            <SortButton
              sortOrder={personaSortOrder}
              onSortChange={setPersonaSortOrder}
              disabled={!!selectedSimulation || isSimulating}
            />
          </div>
          <div id="persona-selector">
            <MultiSelect
              options={visiblePersonas.map((p) => ({
                value: p.id,
                label: p.name,
                short_tag: p.short_tag,
              }))}
              selected={selectedPersonas}
              onChange={(values: string[]) =>
                setSimulationState({
                  selectedPersonas: values,
                  selectedPersonaTab: values[0] || null,
                })
              }
              disabled={!!selectedSimulation || isSimulating}
              placeholder="Choose personas..."
              className="max-h-40 overflow-y-auto"
              popoverClassName="w-[270px]"
              chevronClassName="hidden"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="font-prompt-safe-com-semantic-label text-prompt-safecomvulcan">
            Conversation turns
          </label>
          <Select
            value={conversationTurns}
            onValueChange={setConversationTurns}
            disabled={isSimulating || isMaxTurnsReached()}
          >
            <SelectTrigger
              id="conversation-turns"
              className="w-full bg-prompt-safecomwhite"
            >
              <SelectValue placeholder="Select turns" />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: maxTurns - getMinTurns() + 1 }, (_, i) =>
                (i + getMinTurns()).toString(),
              ).map((turns) => (
                <SelectItem key={turns} value={turns}>
                  {turns} {parseInt(turns) === 1 ? 'turn' : 'turns'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isSimulating ? (
          <Button
            onClick={stopSimulation}
            className="flex w-full items-center gap-2 bg-red-500 text-white hover:bg-red-600"
          >
            <StopCircle className="h-4 w-4" />
            {isStopping ? 'Stopping simulation...' : 'Stop simulation'}
          </Button>
        ) : (
          <Button
            id="start-simulation"
            onClick={startSimulation}
            disabled={
              !selectedAgent || selectedPersonas.length === 0 || isSimulating
            }
            className="hover:bg-prompt-safecomoutrageous-orange/90 flex w-full items-center gap-2 bg-prompt-safecomoutrageous-orange text-white"
          >
            <Play className="h-4 w-4" />
            {selectedSimulation
              ? isMaxTurnsReached()
                ? 'Maximum turns reached'
                : 'Continue simulation'
              : 'Start simulation'}
          </Button>
        )}
        {selectedSimulation && !isSimulating && (
          <Button
            onClick={newSimulation}
            className="bg- flex w-full items-center gap-2 bg-prompt-safecomfantasy text-prompt-safecomoutrageous-orange shadow-none outline outline-prompt-safecomoutrageous-orange hover:bg-prompt-safecomfantasy"
          >
            <SquarePlus className="h-4 w-4" />
            New simulation
          </Button>
        )}

        {simulationProgress && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-prompt-safecomabbey">
              <span>
                Persona {simulationProgress.current} of{' '}
                {simulationProgress.total}
              </span>
              <span>
                Turn {simulationProgress.turn} of{' '}
                {simulationProgress.totalTurns}
              </span>
            </div>
            <Progress
              value={
                (((simulationProgress.current - 1) *
                  simulationProgress.totalTurns +
                  simulationProgress.turn) /
                  (simulationProgress.total * simulationProgress.totalTurns)) *
                100
              }
            />
            <p className="text-center text-sm text-prompt-safecomabbey">
              Simulating conversation with {simulationProgress.persona}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
